import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";

interface SummaryRowsProps {
  pauzeduurPerDay: number[];
  buckets: {
    perDay: { p100: number; p125: number; p150: number; p200: number; total: number }[];
    total: { p100: number; p125: number; p150: number; p200: number; total: number };
  };
  below8hDays: number[];
}

function fmt(min: number): string {
  if (min === 0) return "–";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${m < 10 ? "0" : ""}${m}`;
}

export function SummaryRows({ pauzeduurPerDay, buckets, below8hDays }: SummaryRowsProps) {
  const { t } = useTranslation();
  const has125 = buckets.total.p125 > 0;
  const has150 = buckets.total.p150 > 0;
  const has200 = buckets.total.p200 > 0;
  const totalMin = buckets.total.total;
  const totalAlert = below8hDays.length > 0;
  return (
    <>
      <tr className="text-xs text-ink-700">
        <td colSpan={5} className="pl-8 py-1.5">
          {t("admin.uren.summary.pauzeduur")}
        </td>
        {pauzeduurPerDay.map((m, i) => (
          <td key={i} className="tabular-nums text-right pr-2">
            {fmt(m)}
          </td>
        ))}
        <td className="tabular-nums text-right pr-3">
          {fmt(pauzeduurPerDay.reduce((a, b) => a + b, 0))}
        </td>
      </tr>
      <tr className="bg-p-400/[0.06] text-xs">
        <td colSpan={5} className="pl-8 py-1.5">
          {t("admin.uren.summary.bucket-100")}
        </td>
        {buckets.perDay.map((b, i) => (
          <td key={i} className="tabular-nums text-right pr-2">
            <span className={below8hDays.includes(i) ? "text-vroom-red" : ""}>{fmt(b.p100)}</span>
            {below8hDays.includes(i) && (
              <AlertTriangle
                size={10}
                className="inline ml-1 text-vroom-red"
                aria-label="Below 8h"
              />
            )}
          </td>
        ))}
        <td className={`tabular-nums text-right pr-3 ${totalAlert ? "text-vroom-red" : ""}`}>
          {fmt(totalMin)}
          {totalAlert && <AlertTriangle size={10} className="inline ml-1" />}
        </td>
      </tr>
      {has125 && (
        <tr className="bg-p-500/[0.06] text-xs">
          <td colSpan={5} className="pl-8 py-1.5">
            {t("admin.uren.summary.bucket-125")}
          </td>
          {buckets.perDay.map((b, i) => (
            <td key={i} className="tabular-nums text-right pr-2">
              {fmt(b.p125)}
            </td>
          ))}
          <td className="tabular-nums text-right pr-3">{fmt(buckets.total.p125)}</td>
        </tr>
      )}
      {has150 && (
        <tr className="bg-p-600/[0.06] text-xs">
          <td colSpan={5} className="pl-8 py-1.5">
            {t("admin.uren.summary.bucket-150")}
          </td>
          {buckets.perDay.map((b, i) => (
            <td key={i} className="tabular-nums text-right pr-2">
              {fmt(b.p150)}
            </td>
          ))}
          <td className="tabular-nums text-right pr-3">{fmt(buckets.total.p150)}</td>
        </tr>
      )}
      {has200 && (
        <tr className="bg-p-800/[0.06] text-xs">
          <td colSpan={5} className="pl-8 py-1.5">
            {t("admin.uren.summary.bucket-200")}
          </td>
          {buckets.perDay.map((b, i) => (
            <td key={i} className="tabular-nums text-right pr-2">
              {fmt(b.p200)}
            </td>
          ))}
          <td className="tabular-nums text-right pr-3">{fmt(buckets.total.p200)}</td>
        </tr>
      )}
    </>
  );
}

export { fmt as fmtMinutes };
